import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2, Users, Compass, Handshake, FileText, CreditCard,
  Plus, Pencil, Trash2, Upload, X, Save, Image as ImageIcon, CheckCircle2, RefreshCcw,
  Phone, Mail, MessageSquare
} from "lucide-react";
import { api, formatApiError, formatPrice, PROPERTY_TYPES } from "@/lib/api";

const TABS = [
  { key: "overview", label: "Overview", icon: Users },
  { key: "properties", label: "Properties", icon: Building2 },
  { key: "leads", label: "Leads", icon: MessageSquare },
  { key: "surveys", label: "Survey Requests", icon: Compass },
  { key: "partnerships", label: "Partnerships", icon: Handshake },
  { key: "blog", label: "Blog", icon: FileText },
  { key: "payments", label: "Payments", icon: CreditCard },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#101827]">Admin Dashboard</h1>
          <p className="text-sm text-[#64748B]">Manage listings, leads, content and payments.</p>
        </div>
        <button onClick={loadStats} className="ae-btn-outline text-sm" data-testid="dashboard-refresh"><RefreshCcw className="w-4 h-4" /> Refresh</button>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6 bg-white border border-[#E2E8F0] rounded-xl p-1.5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition ${
              tab === t.key ? "bg-[#101827] text-white" : "text-[#64748B] hover:text-[#101827]"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview stats={stats} />}
      {tab === "properties" && <PropertiesTab onChange={loadStats} />}
      {tab === "leads" && <LeadsTab onChange={loadStats} />}
      {tab === "surveys" && <SurveysTab onChange={loadStats} />}
      {tab === "partnerships" && <PartnershipsTab onChange={loadStats} />}
      {tab === "blog" && <BlogTab />}
      {tab === "payments" && <PaymentsTab />}
    </div>
  );
}

/* ------------------- Overview ------------------- */
function Overview({ stats }) {
  const cards = [
    { label: "Total Properties", value: stats?.total_properties ?? "—", icon: Building2 },
    { label: "Active Listings", value: stats?.active_properties ?? "—", icon: CheckCircle2 },
    { label: "New Enquiries", value: stats?.new_leads ?? "—", icon: MessageSquare },
    { label: "Total Leads", value: stats?.total_leads ?? "—", icon: Users },
    { label: "Survey Requests", value: stats?.total_surveys ?? "—", icon: Compass },
    { label: "Partnership Leads", value: stats?.total_partnerships ?? "—", icon: Handshake },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-xl border border-[#E2E8F0] p-5">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[#64748B]">{c.label}</div>
            <div className="w-9 h-9 rounded-lg bg-[#101827] text-[#C5A46D] flex items-center justify-center"><c.icon className="w-4 h-4" /></div>
          </div>
          <div className="text-3xl font-display font-semibold text-[#101827] mt-2">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ------------------- Properties ------------------- */
const EMPTY_PROPERTY = {
  title: "", property_type: "residential_land", listing_intent: "buy",
  location: "", district: "South 24 Parganas",
  area_value: 0, area_unit: "kottah",
  price: 0, price_label: "Asking Price", price_per_unit: null,
  bedrooms: null, bathrooms: null, facing: "", road_access: "", utilities: "",
  ownership_status: "To be verified", description: "",
  highlights: [], amenities: [], images: [],
  map_url: "", published: true, is_featured: false, is_demo: false,
};

function PropertiesTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/properties").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load properties"));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await api.delete(`/admin/properties/${id}`);
      toast.success("Property deleted");
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-[#101827]">Property Listings</h2>
        <button onClick={() => setEditing({ ...EMPTY_PROPERTY })} className="ae-btn-primary" data-testid="property-add"><Plus className="w-4 h-4" /> Add Property</button>
      </div>
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6F8] text-[#64748B] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Listing</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Location</th>
                <th className="text-right p-3">Price</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-[#E2E8F0]">
                  <td className="p-3">
                    <div className="font-semibold text-[#101827]">{p.title}</div>
                    <div className="text-xs text-[#64748B]">{p.listing_id} · {p.area_value} {p.area_unit}</div>
                  </td>
                  <td className="p-3">{PROPERTY_TYPES.find((t) => t.value === p.property_type)?.label}</td>
                  <td className="p-3">{p.location}</td>
                  <td className="p-3 text-right font-semibold">{formatPrice(p.price)}</td>
                  <td className="p-3">
                    <span className={`ae-chip ${p.published ? "" : "!bg-red-50 !text-red-700 !border-red-100"}`}>
                      {p.published ? "Published" : "Hidden"}
                    </span>
                    {p.is_featured && <span className="ae-chip ml-2">Featured</span>}
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing(p)} className="w-8 h-8 inline-flex items-center justify-center rounded hover:bg-[#F4F6F8]" data-testid={`property-edit-${p.id}`}><Pencil className="w-4 h-4 text-[#183B66]" /></button>
                    <button onClick={() => remove(p.id)} className="w-8 h-8 inline-flex items-center justify-center rounded hover:bg-red-50" data-testid={`property-delete-${p.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#64748B]">No properties yet. Add your first listing.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <PropertyEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onChange?.(); }}
        />
      )}
    </div>
  );
}

function PropertyEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...initial,
    highlights: Array.isArray(initial.highlights) ? initial.highlights.join("\n") : "",
    amenities: Array.isArray(initial.amenities) ? initial.amenities.join("\n") : "",
    images: initial.images || [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = !!initial.id;
  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, images: [...(f.images || []), url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      area_value: Number(form.area_value),
      price: Number(form.price),
      price_per_unit: form.price_per_unit ? Number(form.price_per_unit) : null,
      bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
      bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
      highlights: (form.highlights || "").split("\n").map((s) => s.trim()).filter(Boolean),
      amenities: (form.amenities || "").split("\n").map((s) => s.trim()).filter(Boolean),
    };
    delete payload.listing_id;
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;
    try {
      if (isEdit) await api.put(`/admin/properties/${initial.id}`, payload);
      else await api.post("/admin/properties", payload);
      toast.success(isEdit ? "Property updated" : "Property created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const input = "w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#183B66]";

  return (
    <div className="fixed inset-0 z-[60] bg-[#101827]/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
          <h3 className="font-display text-xl font-semibold text-[#101827]">{isEdit ? "Edit Property" : "Add Property"}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F4F6F8] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
          <input className={input} placeholder="Title *" value={form.title} onChange={u("title")} data-testid="pe-title" />
          <div className="grid sm:grid-cols-3 gap-3">
            <select className={input} value={form.property_type} onChange={u("property_type")} data-testid="pe-type">
              {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select className={input} value={form.listing_intent} onChange={u("listing_intent")} data-testid="pe-intent">
              <option value="buy">For Buyers</option>
              <option value="sell">For Sellers</option>
              <option value="rent">Rent</option>
            </select>
            <input className={input} placeholder="District" value={form.district} onChange={u("district")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className={input} placeholder="Location *" value={form.location} onChange={u("location")} data-testid="pe-location" />
            <input className={input} placeholder="Facing" value={form.facing || ""} onChange={u("facing")} />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <input className={input} type="number" placeholder="Area Value *" value={form.area_value} onChange={u("area_value")} data-testid="pe-area" />
            <select className={input} value={form.area_unit} onChange={u("area_unit")}>
              <option value="kottah">kottah</option>
              <option value="sqft">sqft</option>
              <option value="bigha">bigha</option>
              <option value="acre">acre</option>
              <option value="cottah">cottah</option>
            </select>
            <input className={input} type="number" placeholder="Price (₹) *" value={form.price} onChange={u("price")} data-testid="pe-price" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <input className={input} type="number" placeholder="Bedrooms (optional)" value={form.bedrooms || ""} onChange={u("bedrooms")} />
            <input className={input} type="number" placeholder="Bathrooms (optional)" value={form.bathrooms || ""} onChange={u("bathrooms")} />
            <input className={input} placeholder="Road Access" value={form.road_access || ""} onChange={u("road_access")} />
          </div>
          <input className={input} placeholder="Utilities" value={form.utilities || ""} onChange={u("utilities")} />
          <input className={input} placeholder="Ownership status" value={form.ownership_status || ""} onChange={u("ownership_status")} />
          <textarea className={input + " min-h-[90px]"} placeholder="Description *" value={form.description} onChange={u("description")} data-testid="pe-desc" />
          <textarea className={input + " min-h-[70px]"} placeholder="Highlights (one per line)" value={form.highlights} onChange={u("highlights")} />
          <textarea className={input + " min-h-[70px]"} placeholder="Amenities (one per line)" value={form.amenities} onChange={u("amenities")} />
          <input className={input} placeholder="Map URL (optional Google Maps embed)" value={form.map_url || ""} onChange={u("map_url")} />

          {/* Image uploader */}
          <div>
            <div className="text-xs uppercase tracking-wider text-[#64748B] mb-2">Images</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {(form.images || []).map((im, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-[#F4F6F8] group">
                  <img src={im} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(i)} className="absolute top-1 right-1 w-7 h-7 bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`pe-image-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <label className="aspect-square rounded-lg border-2 border-dashed border-[#CBD5E1] flex flex-col items-center justify-center cursor-pointer hover:border-[#183B66] text-[#64748B] hover:text-[#183B66] text-xs">
                {uploading ? <div className="w-6 h-6 rounded-full border-2 border-[#183B66] border-t-transparent animate-spin" /> : <><Upload className="w-5 h-5 mb-1" /> Upload</>}
                <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="pe-upload" />
              </label>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <label className="inline-flex items-center gap-2 text-sm text-[#1F2937]">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-[#1F2937]">
              <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Featured
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-[#1F2937]">
              <input type="checkbox" checked={form.is_demo} onChange={(e) => setForm({ ...form, is_demo: e.target.checked })} /> Demo Listing
            </label>
          </div>
        </div>
        <div className="p-5 border-t border-[#E2E8F0] flex justify-end gap-2">
          <button onClick={onClose} className="ae-btn-outline" data-testid="pe-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="ae-btn-primary" data-testid="pe-save"><Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Property"}</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------- Leads ------------------- */
const LEAD_STATUSES = ["new", "contacted", "follow_up", "converted", "closed"];

function LeadsTab({ onChange }) {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    const params = {};
    if (status) params.status = status;
    if (search) params.search = search;
    try { const { data } = await api.get("/admin/leads", { params }); setLeads(data); }
    catch { toast.error("Failed to load leads"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, search]);

  const updateStatus = async (id, newStatus) => {
    try { await api.patch(`/admin/leads/${id}`, { status: newStatus }); load(); onChange?.(); toast.success("Status updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveNotes = async (id, notes) => {
    try { await api.patch(`/admin/leads/${id}`, { internal_notes: notes }); toast.success("Note saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <input placeholder="Search by name, phone, email" value={search} onChange={(e) => setSearch(e.target.value)} className="border border-[#E2E8F0] bg-white rounded-lg px-3 py-2 text-sm flex-1 min-w-[220px]" data-testid="lead-search" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-[#E2E8F0] bg-white rounded-lg px-3 py-2 text-sm" data-testid="lead-status-filter">
          <option value="">All statuses</option>
          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {leads.map((l) => (
          <div key={l.id} className="bg-white rounded-xl border border-[#E2E8F0] p-5" data-testid={`lead-row-${l.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="font-semibold text-[#101827]">{l.name}</div>
                <div className="text-sm text-[#64748B] flex items-center gap-3 mt-1">
                  <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {l.phone}</span>
                  {l.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {l.email}</span>}
                </div>
                <div className="text-xs text-[#64748B] mt-1">
                  <span className="ae-chip mr-2">{l.service_type}</span>
                  {l.location && <span className="text-xs">{l.location}</span>}
                  {l.budget && <span className="text-xs"> · {l.budget}</span>}
                </div>
                {l.message && <p className="text-sm text-[#1F2937] mt-3 bg-[#F4F6F8] rounded p-3">{l.message}</p>}
              </div>
              <div className="text-right">
                <div className="text-xs text-[#64748B] mb-2">{new Date(l.created_at).toLocaleString()}</div>
                <select
                  value={l.status}
                  onChange={(e) => updateStatus(l.id, e.target.value)}
                  className="border border-[#E2E8F0] rounded-lg px-3 py-1.5 text-sm bg-white"
                  data-testid={`lead-status-${l.id}`}
                >
                  {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <textarea
                placeholder="Internal note…"
                defaultValue={l.internal_notes || ""}
                onBlur={(e) => { if ((e.target.value || "") !== (l.internal_notes || "")) saveNotes(l.id, e.target.value); }}
                className="w-full border border-[#E2E8F0] rounded-lg p-2 text-sm min-h-[48px]"
                data-testid={`lead-notes-${l.id}`}
              />
            </div>
          </div>
        ))}
        {leads.length === 0 && <div className="p-8 text-center text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl">No leads yet.</div>}
      </div>
    </div>
  );
}

/* ------------------- Surveys ------------------- */
function SurveysTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/survey-requests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, newStatus) => {
    try { await api.patch(`/admin/survey-requests/${id}`, { status: newStatus }); load(); toast.success("Status updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STATUSES = ["new", "contacted", "scheduled", "completed", "closed"];

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[#64748B] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Client</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Location</th>
              <th className="text-left p-3">Survey Type</th>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-[#E2E8F0]">
                <td className="p-3 font-semibold text-[#101827]">{s.name}</td>
                <td className="p-3">{s.phone}</td>
                <td className="p-3">{s.location}{s.mouza ? ` · ${s.mouza}` : ""}</td>
                <td className="p-3">{s.survey_type}</td>
                <td className="p-3 text-xs text-[#64748B]">{s.preferred_date || "—"}</td>
                <td className="p-3">
                  <select value={s.status} onChange={(e) => updateStatus(s.id, e.target.value)} className="border border-[#E2E8F0] bg-white rounded-lg px-2 py-1 text-xs">
                    {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#64748B]">No survey requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------- Partnerships ------------------- */
function PartnershipsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/partnerships").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const STATUSES = ["new", "contacted", "approved", "declined"];
  const updateStatus = async (id, newStatus) => {
    try { await api.patch(`/admin/partnerships/${id}`, { status: newStatus }); load(); toast.success("Status updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {items.map((p) => (
        <div key={p.id} className="bg-white rounded-xl border border-[#E2E8F0] p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-semibold text-[#101827]">{p.name}</div>
              <div className="text-xs text-[#64748B]">{p.organization}</div>
            </div>
            <select value={p.status} onChange={(e) => updateStatus(p.id, e.target.value)} className="border border-[#E2E8F0] bg-white rounded-lg px-2 py-1 text-xs">
              {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div className="text-sm text-[#64748B] mt-2">{p.contact} {p.email ? `· ${p.email}` : ""}</div>
          <div className="text-xs text-[#64748B] mt-1">{p.area_of_operation} · <span className="ae-chip">{p.partnership_category}</span></div>
          {p.message && <p className="text-sm text-[#1F2937] mt-3 bg-[#F4F6F8] rounded p-3">{p.message}</p>}
        </div>
      ))}
      {items.length === 0 && <div className="col-span-full p-8 text-center text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl">No partnership enquiries yet.</div>}
    </div>
  );
}

/* ------------------- Blog ------------------- */
const EMPTY_POST = { title: "", slug: "", excerpt: "", content: "", category: "Land Records", author: "Apurba Estate Team", cover_image: "", published: true };

function BlogTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/blog").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`); toast.success("Post deleted"); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-[#101827]">Blog Posts</h2>
        <button onClick={() => setEditing({ ...EMPTY_POST })} className="ae-btn-primary" data-testid="blog-add"><Plus className="w-4 h-4" /> New Post</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-[#E2E8F0] p-5 flex gap-4" data-testid={`blog-row-${p.id}`}>
            <div className="w-24 h-20 rounded-lg bg-[#F4F6F8] overflow-hidden shrink-0">
              {p.cover_image ? <img src={p.cover_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#64748B]"><ImageIcon className="w-6 h-6" /></div>}
            </div>
            <div className="flex-1">
              <div className="font-semibold text-[#101827] line-clamp-1">{p.title}</div>
              <div className="text-xs text-[#64748B] mt-0.5">{p.category} · /{p.slug}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(p)} className="ae-btn-outline !py-1.5 !px-3 text-xs"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => remove(p.id)} className="ae-btn-outline !py-1.5 !px-3 text-xs !border-red-200 !text-red-600 hover:!bg-red-600 hover:!text-white"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full p-8 text-center text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl">No posts yet.</div>}
      </div>

      {editing && <PostEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PostEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial.id;
  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.title || !form.slug || !form.excerpt || !form.content) { toast.error("Please fill title, slug, excerpt and content."); return; }
    setSaving(true);
    try {
      const payload = { title: form.title, slug: form.slug, excerpt: form.excerpt, content: form.content, category: form.category, author: form.author, cover_image: form.cover_image, published: form.published };
      if (isEdit) await api.put(`/admin/blog/${initial.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(isEdit ? "Post updated" : "Post created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const input = "w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#183B66]";

  return (
    <div className="fixed inset-0 z-[60] bg-[#101827]/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
          <h3 className="font-display text-xl font-semibold text-[#101827]">{isEdit ? "Edit Post" : "New Post"}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F4F6F8] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
          <input className={input} placeholder="Title" value={form.title} onChange={u("title")} />
          <input className={input} placeholder="Slug (e.g. my-article-slug)" value={form.slug} onChange={u("slug")} />
          <div className="grid sm:grid-cols-2 gap-3">
            <input className={input} placeholder="Category" value={form.category} onChange={u("category")} />
            <input className={input} placeholder="Author" value={form.author} onChange={u("author")} />
          </div>
          <input className={input} placeholder="Cover image URL" value={form.cover_image || ""} onChange={u("cover_image")} />
          <textarea className={input + " min-h-[70px]"} placeholder="Excerpt" value={form.excerpt} onChange={u("excerpt")} />
          <textarea className={input + " min-h-[260px] font-mono"} placeholder="Content (Markdown-ish: use ## for H2, - for list)" value={form.content} onChange={u("content")} />
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
          </label>
        </div>
        <div className="p-5 border-t border-[#E2E8F0] flex justify-end gap-2">
          <button onClick={onClose} className="ae-btn-outline">Cancel</button>
          <button onClick={save} disabled={saving} className="ae-btn-primary"><Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Post"}</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------- Payments ------------------- */
function PaymentsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[#64748B] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Order</th>
              <th className="text-left p-3">Client</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-[#E2E8F0]">
                <td className="p-3 font-mono text-xs">{p.order_id}</td>
                <td className="p-3">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-[#64748B]">{p.phone}{p.email ? ` · ${p.email}` : ""}</div>
                </td>
                <td className="p-3 text-right font-semibold">₹ {(p.amount / 100).toFixed(0)}</td>
                <td className="p-3">
                  <span className={`ae-chip ${p.status === "paid" ? "!bg-emerald-50 !text-emerald-700 !border-emerald-200" : ""}`}>{p.status}</span>
                </td>
                <td className="p-3 text-xs text-[#64748B]">{new Date(p.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-[#64748B]">No payments yet. (Add Razorpay keys to backend/.env to activate the paid consultation flow.)</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
